import { useState, useEffect, useCallback, useRef } from 'react';
import { PlayerCooldown } from '../utils/PlayerCooldown';

export function usePlayerCooldown() {
  const cooldownManager = PlayerCooldown.getInstance();
  const [activeCooldowns, setActiveCooldowns] = useState<Map<number, number>>(new Map());
  const intervalRef = useRef<number | null>(null);

  // Update active cooldowns periodically
  useEffect(() => {
    const updateCooldowns = () => {
      const cooldowns = cooldownManager.getAllActiveCooldowns();
      setActiveCooldowns(new Map(cooldowns));
    };

    // Initial update
    updateCooldowns();

    // Setup interval for updates
    intervalRef.current = window.setInterval(updateCooldowns, 250);

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const startCooldown = useCallback((playerId: number) => {
    cooldownManager.setCooldown(playerId);
    setActiveCooldowns(prev => {
      const newMap = new Map(prev);
      newMap.set(playerId, Date.now() + 5000);
      return newMap;
    });
  }, []);

  const checkCooldown = useCallback((playerId: number) => {
    return cooldownManager.isCooldownActive(playerId);
  }, []);

  const getRemainingTime = useCallback((playerId: number) => {
    return cooldownManager.getCooldownRemaining(playerId);
  }, []);

  const clearCooldown = useCallback((playerId: number) => {
    cooldownManager.clearCooldown(playerId);
    setActiveCooldowns(prev => {
      const newMap = new Map(prev);
      newMap.delete(playerId);
      return newMap;
    });
  }, []);

  return {
    startCooldown,
    checkCooldown,
    getRemainingTime,
    clearCooldown,
    activeCooldowns
  };
}